/* eslint-disable @typescript-eslint/no-shadow */

import { type ReactNode, useState } from 'react';
import { Gallery, type GalleryProps } from 'react-photoswipe-gallery';
import styled, { createGlobalStyle, css } from 'styled-components';
import { ColorNordicGrey3, Spacing8, Spacing24, Spacing32 } from 'autogen/design-tokens/tokens';
import { Typography } from 'components/generic/typography';
import useElementSizeEffect from 'hooks/globals/useElementSizeEffect';
import useScrollPositionEffect from 'hooks/globals/useScrollPositionEffect';
import { useIsChanel } from 'hooks/user/useIsBrand';
import { invisibleScrollbar } from 'modules/helpers/mixins';
import { breakpoint, sizes } from 'variables';
import GalleryPagination from './components/GalleryPagination';
import GalleryPreviewSlideRenderer from './components/GalleryPreviewSlideRenderer';
import GalleryThumbnailCarousel from './components/GalleryThumbnailCarousel';
import GalleryThumbnailRenderer from './components/GalleryThumbnailRenderer';
import type {
    GalleryMediaItems,
    GalleryPreviewerOptions,
    onCarouselScrollClick,
    onPaginate,
    onThumbnailClick
} from './shared/types';

// #region Styles
const ThumbnailCarouselContainer = styled.div`
    display: none;

    ${breakpoint.up(sizes.lg)} {
        position: relative;
        display: flex;
        flex-direction: column;
        align-self: start;
        width: 100%;
        height: 100%;
        min-height: 0;
    }
`;

const StyledGalleryPagination = styled(GalleryPagination)<{ $hidePagination?: boolean }>`
    margin-top: ${Spacing8};
    display: ${({ $hidePagination }) => ($hidePagination ? 'none' : 'flex')};
    flex-wrap: wrap;

    ${breakpoint.up(sizes.lg)} {
        display: none;
    }
`;

const Container = styled.div<{ $hideGalleryThumbnailCarousel?: boolean; $isChanel: boolean }>`
    ${({ $hideGalleryThumbnailCarousel, $isChanel }) =>
        $hideGalleryThumbnailCarousel
            ? css`
                display: grid;
                grid-template-columns: 100%;
                grid-template-rows: 100%;

                ${ThumbnailCarouselContainer} {
                    display: none;
                }
              `
            : css`
                display: grid;
                grid-template-columns: 100%;
                grid-template-rows: 100%;

                ${breakpoint.up(sizes.lg)} {
                    column-gap: ${Spacing32};
                    grid-template-columns: 94px 1fr;

                    ${
                        $isChanel &&
                        css`
                            column-gap: ${Spacing24};
                            grid-template-columns: 80px 1fr;
                        `
                    }
                }
            `}
`;

const PreviewContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
`;

const PreviewSlider = styled.ul`
    display: flex;
    scroll-padding-left: 0px;
    width: 100%;
    padding: 0;
    margin: 0;
    white-space: nowrap;
    -ms-overflow-style: none;
    scrollbar-width: none;
    scroll-snap-type: x mandatory;
    overflow-x: scroll;
    scroll-behavior: smooth;

    ${breakpoint.up(sizes.lg)} {
        scroll-behavior: auto;
    }

    ${invisibleScrollbar}
`;

const SlideCounterContainer = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    padding: ${Spacing8};
    opacity: 0.8;
    background-color: ${ColorNordicGrey3};
    z-index: 1;
    pointer-events: none;

    ${breakpoint.up(sizes.lg)} {
        display: none;
    }
`;

const GlobalStyleOverride = createGlobalStyle`
  .pswp--click-to-zoom.pswp--zoom-allowed .pswp__img {
    object-fit: contain;
  }
`;
// #endregion Styles

const defaultOptions: GalleryPreviewerOptions = {
    visibleCarouselThumbnails: 4,
    hideGalleryThumbnailCarousel: false,
    hidePagination: false
};

export function ThumbnailCarousel({
    itemsVisibleInViewport,
    onCarouselScrollClick,
    items,
    onThumbnailClick,
    onThumbnailHover
}) {
    return (
        <ThumbnailCarouselContainer>
            <GalleryThumbnailCarousel
                itemsVisibleInViewport={itemsVisibleInViewport}
                onCarouselScrollClick={onCarouselScrollClick}
            >
                <GalleryThumbnailRenderer
                    items={items}
                    onThumbnailClick={onThumbnailClick}
                    onThumbnailHover={onThumbnailHover}
                />
            </GalleryThumbnailCarousel>
        </ThumbnailCarouselContainer>
    );
}

export function Pagination({ hidePagination, items, activeIndex, onPaginate }) {
    return (
        <StyledGalleryPagination
            $hidePagination={hidePagination}
            items={items}
            activeIndex={activeIndex}
            onPaginate={onPaginate}
        />
    );
}

export function Preview({ items, children, setRefs, currSlideNum, numOfSlides }) {
    return (
        <PreviewContainer>
            <SlideCounterContainer>
                <Typography.Label size="XS">{`${currSlideNum} / ${numOfSlides}`}</Typography.Label>
            </SlideCounterContainer>

            <PreviewSlider ref={setRefs}>
                <GalleryPreviewSlideRenderer items={items} />
            </PreviewSlider>

            {children}
        </PreviewContainer>
    );
}

export default function GalleryMediaPreviewer({
    items,
    options = defaultOptions,
    onOpenCallback,
    onThumbnailClick,
    onPaginate,
    onCarouselScrollClick,
    children,
    ...restProps
}: {
    items: GalleryMediaItems;
    options?: GalleryPreviewerOptions;
    onOpenCallback?: GalleryProps['onOpen'];
    onThumbnailClick?: onThumbnailClick;
    onPaginate?: onPaginate;
    onCarouselScrollClick?: onCarouselScrollClick;
    children?: ReactNode;
}) {
    const { visibleCarouselThumbnails, hideGalleryThumbnailCarousel, hidePagination } = {
        ...defaultOptions,
        ...options
    };
    const [currSlideNum, setCurrSlideNum] = useState(1);
    const [numOfSlides, setNumOfSlides] = useState(1);
    const currSlideIndex = currSlideNum - 1;

    const isChanel = useIsChanel();

    const calcCurrentSlide = (scrollPosX: number, slideWidth: number) =>
        Math.round((scrollPosX + slideWidth) / slideWidth);
    const calcNumOfSlides = (totalScrollWidth: number, slideWidth: number) => Math.round(totalScrollWidth / slideWidth);

    /* every time the ref element changes size, run the effect (recalculate scrollLeft)  */
    const [refSize] = useElementSizeEffect<HTMLUListElement>(({ width, scrollWidth }) => {
        if (refSize.current) {
            refSize.current.scrollLeft = currSlideIndex * width;
            setNumOfSlides(calcNumOfSlides(scrollWidth, width));
        }
    }, []);

    const setSlide = (index) => {
        if (refSize.current) {
            const { width } = refSize.current.getBoundingClientRect();
            const { scrollWidth } = refSize.current;
            refSize.current.scrollLeft = index * width;
            setNumOfSlides(calcNumOfSlides(scrollWidth, width));
        }
    };

    /* every time the ref element changes scrollPosition, run the effect (recalculate CurrSlideNum)  */
    const [refScroll] = useScrollPositionEffect<HTMLUListElement>(({ currPos: { x: scrollPosX } }) => {
        if (refScroll.current) {
            const nextCurrSlide = calcCurrentSlide(scrollPosX, refScroll.current.offsetWidth);
            setCurrSlideNum(nextCurrSlide);
        }
    }, []);

    /* set multiple refs on a single element */
    const setRefs = (element: HTMLUListElement) => {
        refSize.current = element;
        refScroll.current = element;
    };

    return (
        <Container $hideGalleryThumbnailCarousel={hideGalleryThumbnailCarousel} $isChanel={isChanel} {...restProps}>
            <GlobalStyleOverride />

            <Gallery
                onOpen={onOpenCallback}
                options={{ hideAnimationDuration: 0, showAnimationDuration: 0, zoomAnimationDuration: 0 }}
            >
                <ThumbnailCarousel
                    itemsVisibleInViewport={visibleCarouselThumbnails}
                    onCarouselScrollClick={onCarouselScrollClick}
                    items={items}
                    onThumbnailClick={onThumbnailClick}
                    onThumbnailHover={(index) => setSlide(index)}
                />

                <Preview items={items} setRefs={setRefs} currSlideNum={currSlideNum} numOfSlides={numOfSlides}>
                    {children}

                    <Pagination
                        hidePagination={hidePagination}
                        items={items}
                        activeIndex={currSlideIndex}
                        onPaginate={(index, type) => {
                            setSlide(index);
                            if (onPaginate) {
                                onPaginate(index, type);
                            }
                        }}
                    />
                </Preview>
            </Gallery>
        </Container>
    );
}
